import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, COMPANY } from "../data/site";

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [location.pathname]);

    return (
        <>
            <header
                data-testid="site-header"
                className={`fixed inset-x-0 top-0 z-50 bg-white/95 backdrop-blur-md transition-all duration-300 ${
                    scrolled ? "shadow-[0_8px_30px_rgba(15,18,22,0.12)] border-b border-slate-200" : "border-b border-transparent"
                }`}
            >
                <div className="mx-auto flex max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-10">
                    <Link to="/" data-testid="header-logo-link" className={`transition-all duration-300 ${scrolled ? "py-2.5" : "py-4"}`}>
                        <Logo compact={scrolled} />
                    </Link>
                    <nav className="hidden xl:flex items-center gap-5" data-testid="desktop-nav">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className={({ isActive }) =>
                                    `relative whitespace-nowrap font-display text-[12px] font-semibold tracking-[0.12em] transition-colors duration-200 hover:text-brand-red ${
                                        isActive ? "text-brand-red" : "text-brand-charcoal"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/get-a-quote"
                            data-testid="nav-get-a-quote"
                            className="group relative ml-2 whitespace-nowrap overflow-hidden bg-brand-red px-5 py-2.5 font-display text-[12px] font-bold tracking-[0.14em] text-white transition-colors duration-300 hover:bg-brand-charcoal"
                        >
                            GET A QUOTE
                        </Link>
                    </nav>
                    <button
                        data-testid="mobile-menu-button"
                        onClick={() => setOpen(!open)}
                        className="xl:hidden p-2 text-brand-charcoal"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={26} /> : <Menu size={26} />}
                    </button>
                </div>
                {open && (
                    <div className="xl:hidden border-t border-slate-200 bg-white px-6 py-6" data-testid="mobile-menu">
                        <nav className="flex flex-col gap-1">
                            {NAV_LINKS.map((l) => (
                                <NavLink
                                    key={l.to}
                                    to={l.to}
                                    data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                                    className={({ isActive }) =>
                                        `border-b border-slate-100 py-3 font-display text-sm font-semibold tracking-[0.12em] ${isActive ? "text-brand-red" : "text-brand-charcoal"}`
                                    }
                                >
                                    {l.label}
                                </NavLink>
                            ))}
                            <Link
                                to="/get-a-quote"
                                data-testid="mobile-nav-get-a-quote"
                                className="mt-4 bg-brand-red px-5 py-3.5 text-center font-display text-sm font-bold tracking-[0.14em] text-white"
                            >
                                GET A QUOTE
                            </Link>
                        </nav>
                    </div>
                )}
            </header>
            <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 xl:hidden" data-testid="mobile-quick-actions">
                <a href={COMPANY.whatsapp} target="_blank" rel="noreferrer" data-testid="mobile-whatsapp-btn"
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg">
                    <MessageCircle size={22} />
                </a>
                <a href={`tel:${COMPANY.phone1.replace(/\s/g, "")}`} data-testid="mobile-call-btn"
                    className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-red text-white shadow-lg">
                    <Phone size={20} />
                </a>
            </div>
        </>
    );
};
